import { useId } from 'react'
import { Badge, type BadgeTone } from '@/components/ui/badge'
import { TrendDownIcon, TrendUpIcon } from '@/components/ui/icons'
import { cn } from '@/lib/utils'

interface SampleRow {
  category: string
  /** Bar fill and median tick positions, as a percentage of the track. */
  score: number
  median: number
  delta: string
  tier: string
  tierTone: BadgeTone
}

// Illustrative figures only: the page sells the assessment before an organization has one.
const SAMPLE: SampleRow[] = [
  {
    category: 'Written Standards',
    score: 90,
    median: 57,
    delta: '+1.00',
    tier: 'Leading',
    tierTone: 'success',
  },
  {
    category: 'Program Structure & Oversight',
    score: 80,
    median: 60,
    delta: '+0.60',
    tier: 'Leading',
    tierTone: 'success',
  },
  {
    category: 'Third-Party Risk Management',
    score: 57,
    median: 67,
    delta: '-0.30',
    tier: 'Maturing',
    tierTone: 'submitted',
  },
  {
    category: 'Risk Assessment & Auditing',
    score: 30,
    median: 63,
    delta: '-1.00',
    tier: 'Foundational',
    tierTone: 'danger',
  },
]

export function BenchmarkPreview() {
  const captionId = useId()
  const weakest = SAMPLE[SAMPLE.length - 1]
  return (
    <figure
      aria-labelledby={captionId}
      className="rounded-lg border border-border bg-surface-page p-6"
    >
      <figcaption
        id={captionId}
        className="label mb-3 font-semibold uppercase tracking-[0.12em] text-body"
      >
        Score vs. benchmark <span className="sr-only">(example)</span>
      </figcaption>
      <ul className="divide-y divide-border border-t border-border">
        {SAMPLE.map((row) => {
          const above = row.score >= row.median
          const Trend = above ? TrendUpIcon : TrendDownIcon
          return (
            <li
              key={row.category}
              className={cn('-mx-3 px-3 py-4', row === weakest && 'rounded-lg bg-danger-bg')}
            >
              <div className="mb-2 flex items-baseline justify-between gap-3">
                <span className="font-semibold text-heading">{row.category}</span>
                <span className="flex items-center gap-1 font-semibold text-heading">
                  <Trend className={above ? 'text-success' : 'text-danger'} />
                  {row.delta}
                </span>
              </div>
              <div className="flex items-center gap-4">
                <div aria-hidden="true" className="relative h-2.5 flex-1 rounded-full bg-border">
                  <div
                    className={cn(
                      'absolute inset-y-0 left-0 rounded-full',
                      above ? 'bg-success' : 'bg-danger',
                    )}
                    style={{ width: `${row.score}%` }}
                  />
                  <div
                    className="absolute -inset-y-0.5 w-0.5 rounded-full bg-primary"
                    style={{ left: `${row.median}%` }}
                  />
                </div>
                <Badge tone={row.tierTone}>{row.tier}</Badge>
              </div>
            </li>
          )
        })}
      </ul>
      <div aria-hidden="true" className="label mt-4 flex flex-wrap gap-x-6 gap-y-1 text-body">
        <span className="flex items-center gap-2">
          <span className="h-3 w-3 rounded-[2px] bg-success" />
          Your score
        </span>
        <span className="flex items-center gap-2">
          <span className="h-3.5 w-0.5 rounded-full bg-primary" />
          Peer benchmark median
        </span>
      </div>
    </figure>
  )
}
